<template>
  <b-row class="base-row-padded">
    <b-col>
      <b-row>
        <b-col cols="4">
          <b-button variant="light" :to="`/admin/${playlistType}`"> &lt; Back to Playlists</b-button>
        </b-col>
        <b-col cols="8" class="d-flex justify-content-end" v-if="permission('playlist-change')">
          <b-button :disabled="formLoading" class="ml-2" variant="success" @click="handleSubmit">Save</b-button>
          <b-button :disabled="formLoading" v-if="id !== 'new'" class="ml-2" variant="danger" @click="deletePlaylistProxy">Delete</b-button>
        </b-col>
      </b-row>
      <h2>{{pageTitle}}</h2>
      <b-form @submit.prevent="handleSubmit" v-if="id === 'new' || playlist">

        <b-form-group label="Name:">
          <b-form-input :disabled="!permission('playlist-change')" v-model="playlist.name" required placeholder="Name"/>
          <b-form-invalid-feedback
            v-if="formErrors && formErrors.name && formErrors.name.length > 0" :state="false">
            <p v-for="(e, $index) in formErrors.name" :key="$index">{{e}}</p>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Slide Duration (minutes):" v-if="playlistType === 'loop-playlists'">
          <b-form-input :disabled="!permission('playlist-change')" type="number" v-model="playlist.duration" required placeholder="Duration of a single slide"/>
          <b-form-invalid-feedback
            v-if="formErrors && formErrors.duration && formErrors.duration.length > 0" :state="false">
            <p v-for="(e, $index) in formErrors.duration" :key="$index">{{e}}</p>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Fade Length (seconds):">
          <b-form-input :disabled="!permission('playlist-change')" type="number" v-model="playlist.fade" required placeholder="Fade length between the slides"/>
          <b-form-invalid-feedback
            v-if="formErrors && formErrors.fade && formErrors.fade.length > 0" :state="false">
            <p v-for="(e, $index) in formErrors.fade" :key="$index">{{e}}</p>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Orientation" :disabled="!permission('playlist-change')">
          <b-form-radio v-model.number="playlist.orientation" value='0'>Landscape</b-form-radio>
          <b-form-radio v-model.number="playlist.orientation" value='1'>Portrait</b-form-radio>
          <b-form-invalid-feedback
            v-if="formErrors && formErrors.orientation && formErrors.orientation.length > 0" :state="false">
            <p v-for="(e, $index) in formErrors.orientation" :key="$index">{{e}}</p>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <b-card header="Playlist Status">
            <b-row>
              <b-col cols="12" sm="6">
                  <b-checkbox v-model="playlist.status" :value="1" :unchecked-value="0" disabled>Published</b-checkbox>
              </b-col>
            </b-row>
          </b-card>
        </b-form-group>

        <div v-if="playlist.days">
          <b-button-group>
            <b-button
            v-for="(day, idx) in playlist.days"
            :key="idx"
            :variant="selectedDay === day ? 'light' : ''"
            @click="selectedDay = day">
              {{weekdayName(idx)}}
            </b-button>
          </b-button-group>
        </div>
        <b-form-group>
          <b-card header-tag="header">
            <template #header>
              <div v-bind:class="{'table-danger': slidesWarning}">
                Slides
              </div>
            </template>
            <b-row>
              <b-col>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>Reorder</th>
                        <th>Slide preview</th>
                        <th>Slide name</th>
                        <th v-if="playlistType === 'playlists'">Duration</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="slide in slidesTarget">
                        <SlideComponent :slide="slide" :key="slide.cid"
                            @move-up="reorderSlide(slide, 'up')"
                            @move-down="reorderSlide(slide, 'down')"
                            @remove-self="removeSlide(slide)"
                            :playlist-type="playlistType"
                            :orientation="playlist.orientation"
                            :disabled="!permission('playlist-change')"/>
                      </template>
                      <SlideComponent v-if="permission('playlist-change')"
                        :slide="emptySlide"
                        :isNew="true" @add-self="addSlide"
                        :key="emptySlide.cid"
                        :playlist-type="playlistType"
                        :orientation="playlist.orientation"/>
                    </tbody>
                  </table>
                </div>
              </b-col>
            </b-row>
          </b-card>
          <b-form-invalid-feedback
            v-if="formErrors && formErrors.slides && formErrors.slides.length > 0" :state="false">
            <p v-for="(e, $index) in formErrors.slides" :key="$index">{{e}}</p>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group v-if="playlist.video_url && playlist.video_url.length > 0" label="Video:">
          <b-card header="Video">
            <b-row>
              <b-col>
                  <video :src="playlist.video_url + '?' + Date.now()" controls type="video/mp4" playsinline></video>
              </b-col>
            </b-row>
          </b-card>
        </b-form-group>

      </b-form>

    </b-col>
  </b-row>
</template>

<script>
import SlideComponent from '@/components/SlideComponent.vue';
import formsMixin from '@/mixins/forms';
import rolesMixin from '@/modules/rolesMixin';
import playlistsMixin from './playlistsMixin';
import { playlistTitles } from '@/types/playlistTypes';
import { weekdaysAsArray } from '@/types/weekdays';

let counter = 0;

export default {
  components: { SlideComponent },
  mixins: [formsMixin, playlistsMixin, rolesMixin],
  data() {
    return {
      playlist: {
        id: null,
      },
      emptySlide: null,
      playlistType: 'playlists',
      selectedDay: null,
    };
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
    playlistObj: {
      type: [Object],
      required: false,
    },
  },
  watch: {
    playlistObj: {
      deep: true,
      handler() {
        this.created();
      },
    },
  },
  created() {
    if (this.$route.meta.playlistType) this.playlistType = this.$route.meta.playlistType;

    this.resetEmptySlide();
    if (this.id !== 'new') {
      this.getPlaylistProxy();
    } else {
      const playlist = {
        name: '',
        fade: 5,
        orientation: 0,
        status: 0,
        ready: 0,
      };
      if (['playlists', 'loop-playlists'].includes(this.playlistType)) {
        playlist.slides = [];
      } else if (['weekly-playlists'].includes(this.playlistType)) {
        playlist.days = Array(7).fill(null).map(() => ({ slides: [] }));
        [this.selectedDay] = playlist.days;
      }
      this.playlist = playlist;
    }
  },
  methods: {
    getPlaylistProxy() {
      if (!this.playlistObj) {
        this.getPlaylist({ id: this.id })
          .then((data) => {
            this.convertData(data);
          })
          .catch(() => {});
      } else {
        this.convertData(this.playlistObj);
      }
    },
    handleSubmit() {
      const playlistObject = { ...this.playlist };
      delete playlistObject.ready;

      const playlistTimeConvertor = (ps) => {
        // time conversion for slides
        // eslint-disable-next-line no-param-reassign
        ps.slides = ps.slides.map((s) => {
          const newSlide = { ...s };
          if (['playlists', 'weekly-playlists'].includes(this.playlistType)) {
            newSlide.starts_at = `${newSlide.starts_at.h}:${newSlide.starts_at.m}:${newSlide.starts_at.s}`;
            newSlide.ends_at = `${newSlide.ends_at.h}:${newSlide.ends_at.m}:${newSlide.ends_at.s}`;
          }
          return newSlide;
        });
      };
      if (playlistObject.slides) playlistTimeConvertor(playlistObject);
      else if (playlistObject.days) {
        playlistObject.days = playlistObject.days.map(ps => ({ ...ps }));
        playlistObject.days.forEach(ps => playlistTimeConvertor(ps));
      }

      if (this.id === 'new') {
        this.createPlaylist(playlistObject)
          .then(() => {})
          .catch(() => {});
      } else {
        this.updatePlaylist(playlistObject).then((updatedPlaylist) => {
          this.convertData(updatedPlaylist);
        }).catch(() => {});
      }
    },
    deletePlaylistProxy() {
      this.deletePlaylist(this.playlist).then(() => {
        this.$router.push({ name: 'playlists' });
      })
        .catch(() => {});
    },
    convertData(data) {
      const newPlaylist = { ...data };

      const slidesConvertor = (ps) => {
        // eslint-disable-next-line no-param-reassign
        ps.slides = ps.slides.map((e) => {
          const convertedObj = {
            cid: counter,
            file_id: e.pivot.file_id,
            duration: e.pivot.duration,
          };
          if (['playlists', 'weekly-playlists'].includes(this.playlistType)) {
            convertedObj.starts_at = {
              h: e.pivot.starts_at.split(':')[0],
              m: e.pivot.starts_at.split(':')[1],
              s: e.pivot.starts_at.split(':')[2],
            };
            convertedObj.ends_at = {
              h: e.pivot.ends_at.split(':')[0],
              m: e.pivot.ends_at.split(':')[1],
              s: e.pivot.ends_at.split(':')[2],
            };
          }
          if (e.type === 'video') {
            convertedObj.duration = e.duration;
          }
          convertedObj.file = e;
          delete convertedObj.file.pivot;
          counter += 1;
          return convertedObj;
        });
      };

      if (newPlaylist.slides) {
        // regular playlist
        slidesConvertor(newPlaylist);
      } else if (newPlaylist.days) {
        newPlaylist.days.forEach(ps => slidesConvertor(ps));
      }

      this.playlist = newPlaylist;
      if (this.playlist.days) [this.selectedDay] = this.playlist.days;
      this.resetEmptySlide();
    },
    reorderSlide(slide, direction) {
      const idx = this.slidesTarget.indexOf(slide);
      if (direction === 'up' && idx > 0) {
        this.slidesTarget.splice(idx, 1);
        this.slidesTarget.splice(idx - 1, 0, slide);
      } else if (direction === 'down' && idx < this.slidesTarget.length - 1) {
        this.slidesTarget.splice(idx, 1);
        this.slidesTarget.splice(idx + 1, 0, slide);
      }
    },
    removeSlide(slide) {
      const idx = this.slidesTarget.indexOf(slide);
      this.slidesTarget.splice(idx, 1);
    },
    resetEmptySlide() {
      this.emptySlide = this.getEmptySlide();
    },
    getEmptySlide() {
      counter += 1;
      let newSlideObj = {
        cid: counter,
        file_id: null,
        file: null,
      };

      if (['playlists', 'weekly-playlists'].includes(this.playlistType)) {
        newSlideObj = {
          ...newSlideObj,
          starts_at: {
            h: '09',
            m: '00',
            s: '00',
          },
          ends_at: {
            h: '20',
            m: '00',
            s: '00',
          },
        };
      }
      return newSlideObj;
    },
    // eslint-disable-next-line camelcase
    addSlide({ file_id, file }) {
      this.slidesTarget.push({
        ...this.getEmptySlide(),
        file_id,
        file,
      });
      counter += 1;
      this.resetEmptySlide();
    },
    weekdayName(idx) {
      return weekdaysAsArray[idx];
    },
  },
  computed: {
    slidesWarning() {
      return this.slidesTarget && this.slidesTarget.find(slide => slide.file.type !== 'url' && slide.file.portrait !== this.playlist.orientation);
    },
    pageTitle() {
      return playlistTitles[this.playlistType];
    },
    slidesTarget() {
      if (this.playlist && this.playlist.slides) return this.playlist.slides;
      if (this.selectedDay && this.selectedDay.slides) return this.selectedDay.slides;
      return null;
    },
  },
};
</script>

<style lang="less" scoped>
video{
  max-width: 100%;
}
.card-header {
  padding: 0px !important;
  div {
    padding: 0.75rem 1.25rem;
    overflow: hidden;
    border-radius: calc(0.25rem - 1px) calc(0.25rem - 1px) 0 0;
  }
}

</style>
